import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import i18n from '../assets/languages/configs';

import FujiModal from '../components/fuji-modal/FujiModal';
import { getValueByLanguage, isEmptyObject, showNotificationDialog } from '../utils';
import { TIMEOUT_DURATION } from '../utils/Constants';
import useR05Api from '../api/useR05Api';
import { ErrorType, MultilingualType, OrderTicket, OrderTicketList, SpotInfo } from 'types';

/**
 * スポットコードによるチケット使用についてのカスタムフック
 */
const useUsageSpotTicket = () => {
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [orderTicket, setOrderTicket] = useState<OrderTicket>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { fetchOrderDetail, usageSpotTicket: usageTicket } = useR05Api();
    const { language } = i18n;
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    const showErrorNotification = useCallback(
        (errorText: string) => {
            showNotificationDialog('error', '', t('notification.title'), t(errorText));
        },
        [t],
    );

    //チケットを使う失敗の時エラーメッセージが表示される処理;
    const handleShowError = useCallback(
        (error: ErrorType) => {
            switch (error.errorType) {
                case 'out_of_date_order': //使用可能期限切れ
                    showErrorNotification(t('error-message.activation.out-of-date'));
                    break;
                case 'cannot_activate_order_today': // 有効化不可曜日エラー有効化不可日エラー
                case 'today_is_exceptional_day':
                case 'cannot_activate_order_holiday':
                    showErrorNotification(t('error-message.activation.disable-date'));
                    break;
                case 'cannot_activate_order_at_now': // 有効化可能時間外エラー
                    showErrorNotification(t('error-message.activation.disable-daytime'));
                    break;
                case 'using_number_is_more_than_bought_ticket': // 使用可能数を超過
                    showErrorNotification(t('error-message.activation.exceeded-quantity'));
                    break;
                case 'undefined_parameter': //4xxエラー
                case 'invalid_parameter':
                case 'not_found_spot_id_and_ticket_id':
                case 'not_found_order_ticket_id':
                case 'order_status_is_not_bought':
                case 'not_found_bought_ticket':
                case 'out_of_date_token':
                case 'invalid_signature_token':
                case 'invalid_api_key':
                    showErrorNotification(t('error-message.activation.failed'));
                    break;
                case 'unexpected': //5xxエラー
                    showErrorNotification(t('error-message.activation.unexpected-error'));
                    break;

                default:
                    showErrorNotification(t('error-message.activation.failed'));
                    break;
            }
        },
        [showErrorNotification, t],
    );

    /**
     * チケットを使用できるかの事前チェック
     */
    const checkUsageSpotTicket = useCallback(
        async (orderTicket: OrderTicket | OrderTicketList, navigation?: boolean) => {
            if (navigator.onLine) {
                let timeoutId;

                try {
                    timeoutId = setTimeout(() => {
                        setLoading(false);
                        showErrorNotification(t('error-message.tickets.fetch-info-failed'));
                    }, TIMEOUT_DURATION);

                    setLoading(true);
                    let data = await fetchOrderDetail({
                        orderId: orderTicket.orderId,
                        orderTicketId: orderTicket.orderTicketId,
                    });
                    setLoading(false);
                    clearTimeout(timeoutId);
                    if (data == null) {
                        return;
                    }
                    data = { ...data, ...data.comboItems[0] };
                    if (navigation) {
                        navigate('/bought/code', { state: { orderTicket: data } });
                    } else {
                        setIsConfirmModalOpen(true);
                        setOrderTicket(data);
                    }
                } catch {
                    clearTimeout(timeoutId);
                    setLoading(false);
                    showErrorNotification(t('error-message.tickets.fetch-info-failed'));
                }
            } else {
                showErrorNotification(t('error-message.server-error'));
            }
        },
        [fetchOrderDetail, navigate, showErrorNotification, t],
    );

    /**
     * チケットを使う
     */
    const usageSpotTicket = useCallback(
        async ({
            orderTicket,
            spotInfo,
            numberOfUseTicket,
        }: {
            orderTicket?: OrderTicket;
            spotInfo: SpotInfo;
            numberOfUseTicket: number;
        }) => {
            setIsConfirmModalOpen(false);

            const { spotId } = spotInfo ?? {};

            //実際に使います
            // call API
            if (navigator.onLine) {
                if (!orderTicket) {
                    showErrorNotification(t('error-message.activation.failed'));
                    return;
                }
                let timeoutId;

                try {
                    timeoutId = setTimeout(() => {
                        setLoading(false);
                        showErrorNotification(t('error-message.activation.timeout-error'));
                    }, TIMEOUT_DURATION);

                    setLoading(true);
                    await usageTicket({
                        spotId: spotId,
                        orderTicketId: orderTicket.orderTicketId,
                        usingNumber: numberOfUseTicket,
                    });
                    clearTimeout(timeoutId);
                    setLoading(false);
                    navigate('/bought/spot-result', {
                        state: { usedTicketQuantity: numberOfUseTicket, orderTicket: orderTicket, spotInfo: spotInfo },
                        replace: true,
                    });
                } catch (error) {
                    clearTimeout(timeoutId);
                    setLoading(false);
                    handleShowError(error as ErrorType);
                }
            } else {
                showErrorNotification(t('error-message.server-error'));
            }
        },
        [handleShowError, navigate, showErrorNotification, t, usageTicket],
    );

    /**
     * チケットを使う（モーダル確認）
     */
    const confirmUsageSpotTicket = useCallback(() => {
        setIsConfirmModalOpen(true);
    }, []);

    //確認モーダルを閉じの処理
    const confirmModalClose = useCallback(() => {
        setIsConfirmModalOpen(false);
    }, []);

    /**
     * 確認モーダル
     */
    const ActivateConfirmModal = useCallback(
        ({ numberOfUseTicket, spotInfo }: { numberOfUseTicket: number; spotInfo: SpotInfo }) => {
            const confirmMessage =
                isEmptyObject(orderTicket) || isEmptyObject(orderTicket?.activateMessage)
                    ? t('modal.activate-confirmation')
                    : l(orderTicket?.activateMessage);
            return (
                <FujiModal
                    isOpen={isConfirmModalOpen}
                    onCancel={confirmModalClose}
                    txtCancel={t('modal.activate-cancel') ?? ''}
                    onSubmit={() => void usageSpotTicket({ orderTicket, numberOfUseTicket, spotInfo })}
                    txtSubmit={t('modal.activate-use')}
                    body={confirmMessage}
                />
            );
        },
        [confirmModalClose, isConfirmModalOpen, l, orderTicket, t, usageSpotTicket],
    );

    return {
        loading,
        checkUsageSpotTicket,
        confirmUsageSpotTicket,
        usageSpotTicket,
        ActivateConfirmModal,
    };
};

export default useUsageSpotTicket;
